import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useMemo } from 'react'

import TanstackTable from 'components/reusable/table/TanstackTable'
import { type ChartConfig } from 'components/ui/chart'

import NeedsAttentionPieChart from './NeedsAttentionPieChart'
import { type NAVStatisticsProps, type StatisticsType } from './types'
import { colors } from '../../../../../config/tailwind/colors'

const chartConfig: ChartConfig = {
  status: {
    label: 'Status',
  },
  items: {
    label: 'NAV Items',
  },
  active: {
    label: 'Active',
    color: colors.primitive.thatch[800],
  },
  silenced: {
    label: 'Silenced',
    color: colors.primitive.thatch[700],
  },
  resolved: {
    label: 'Resolved',
    color: colors.primitive.thatch[600],
  },
}

function getChartDataFromStatistics (
  byStateStats: Record<string, number> | undefined
): StatisticsType[] {
  return Object.entries(byStateStats || {}).map(([key, value]) => ({
    status: key,
    items: value,
  }))
}

export const NAVStatusPieChart = ({
  stats: byStateStats,
}: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getChartDataFromStatistics(byStateStats)
  }, [byStateStats])

  return (
    <NeedsAttentionPieChart
      title='Status'
      nameKey='status'
      dataKey='items'
      chartData={chartData}
      chartConfig={chartConfig}
    />
  )
}

const columnHelper = createColumnHelper<StatisticsType>()

const columns = [
  columnHelper.accessor('status', {
    header: 'Status',
    cell: (info) => chartConfig[info.getValue() as string].label,
  }),
  columnHelper.accessor('items', {
    header: () => <div className='text-end'>Count</div>,
    cell: (info) => <div className='text-end'>{info.getValue()}</div>,
  }),
]

export const NAVStatusTable = ({ stats: byStateStats }: NAVStatisticsProps) => {
  const chartData: StatisticsType[] = useMemo(() => {
    return getChartDataFromStatistics(byStateStats)
  }, [byStateStats])

  const table = useReactTable({
    data: chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex: 0, pageSize: 3 } },
  })

  return (
    <div>
      <div className='flex items-center justify-center mb-4'>
        <div className='font-semibold'>Status</div>
      </div>
      <TanstackTable
        table={table}
        paginationLabelSingular='item'
        showPageSelectionIfSinglePage={false}
      />
    </div>
  )
}
